import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";

import Box from "./Box.jsx";
import ClickableIcon from "./ClickableIcon.jsx";
import handleComponentsProp from "./handleComponentsProp";
import Heading from "./Heading.jsx";
import parseStyle from "./parseStyle.js";
import renderIcon from "./renderIcon.jsx";
import useClickable from "./useClickable.jsx";
import useThemeProps from "./useThemeProps.jsx";

function defaultIconMapping() {
  return null;
}

const DefaultTitle = styled(Heading)`
  font-weight: inherit;
  line-height: inherit;
  font-size: inherit;
`;

function DefaultAction(inProps) {
  const theme = useTheme();
  const { Element, props } = useClickable(inProps);
  let {
    ownerState: { severity },
    round,
    style,
    ...restProps
  } = props;
  return (
    <Element
      css={css`
        /* RESET */
        display: inline-block;
        border: none;
        margin: 0;
        padding: 0;
        width: auto;
        overflow: visible;

        background: transparent;

        /* inherit font & color from ancestor */
        color: inherit;
        font: inherit;
        text-decoration: inherit;
        text-align: inherit;

        /* Normalize line-height. Cannot be changed from normal in Firefox 4+. */
        line-height: normal;

        /* Corrects font smoothing for webkit */
        -webkit-font-smoothing: inherit;
        -moz-osx-font-smoothing: inherit;

        /* Corrects inability to style clickable input types in iOS */
        appearance: none;

        border: 1px solid transparent;
        cursor: pointer;

        padding-block: ${theme.getLength(1.5)};
        padding-inline: ${theme.getLength(round ? 1.5 : 2)};
        border-radius: ${round ? "9999px" : theme.getLength(1)};

        align-self: center;

        &:hover {
          background-color: ${theme.getColor(`${severity}.200`)};
        }

        ${parseStyle(style, theme)}
      `}
      {...restProps}
    />
  );
}

export default function Alert(props) {
  const theme = useTheme();
  let {
    severity = "error",
    color = severity,
    title,
    description,
    children,
    iconSize = 6,
    icon,
    iconMapping,
    action,
    actions = [],
    onClose,
    components,
    ...restProps
  } = useThemeProps({
    props,
    name: "Alert",
  });
  let { Action, Inner, Title } = handleComponentsProp(components, {
    Action: DefaultAction,
    Inner: "div",
    Title: DefaultTitle,
  });

  const { t } = useTranslation();

  const ownerState = {
    severity,
    color,
    title,
    description,
    iconSize,
    icon,
    action,
    onClose,
    ...restProps,
  };

  if (typeof action === "object" && action !== null) {
    let { label: actionLabel, ...actionProps } = action;
    actions = [
      ...actions,
      <Action key="action" {...actionProps} ownerState={ownerState}>
        {actionLabel}
      </Action>,
    ];
  }

  if (onClose) {
    actions = [
      ...actions,
      <ClickableIcon
        key="close"
        name="close"
        label={t("wsui.base.alert.close")}
        color={color}
        size={5}
        onClick={onClose}
      />,
    ];
  }

  if (!icon) {
    icon =
      typeof iconMapping === "function"
        ? iconMapping(ownerState, defaultIconMapping)
        : iconMapping?.[severity];
  }

  return (
    <Box color={color} {...restProps}>
      <Inner>
        <div
          css={css`
            display: flex;
            gap: ${theme.getLength(3)};
            align-items: center;
          `}
        >
          {icon &&
            renderIcon(icon, {
              color: [color, "icon"],
              size: iconSize,
              style: { flex: "none", alignSelf: "start" },
            })}
          <div
            css={css`
              width: 100%;
            `}
          >
            {!!title && <Title>{title}</Title>}
            {children ? (
              typeof children === "function" ? (
                children(ownerState)
              ) : (
                children
              )
            ) : (
              <p
                css={css`
                  margin: 0;
                  font-weight: 300;
                `}
              >
                {description}
              </p>
            )}
          </div>
          <div
            css={css`
              align-self: start;
              display: flex;
            `}
          >
            {actions}
          </div>
        </div>
      </Inner>
    </Box>
  );
}
